import { ReactNode } from 'react';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import classNames from 'classnames';

import { useIsDesktop } from '../../hooks';
import { Box } from '../../Box';
import { Typography } from '../../Typography';
import { Tooltip } from '../../Tooltip';
import { Icon } from '../../Icon';
import { Label } from '../common';

import { useStyles } from './RadioInput.styles';

/**
 * Свойства компонента {@link RadioInput}
 */
interface RadioInputProps {
  /**
   * Массив элементов, каждый из которых содержит метку и значение.
   */
  items: {
    label: string;
    value: string | number;
    disabled?: boolean;
    tooltip?: string;
  }[];

  /**
   * Метка для группы радио-ввода.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Размещение метки относительно радиокнопки.
   */
  labelPlacement?: 'start' | 'end' | 'top';

  /**
   * Текущее выбранное значение.
   */
  value: string | number | undefined;

  /**
   * Функция обратного вызова, которая вызывается при изменении выбранного значения.
   */
  onChange: (value: string | number) => void;

  /**
   * Определяет, является ли группа радио-ввода обязательной.
   */
  required?: boolean;

  /**
   * Определяет, должны ли радиокнопки отображаться в ряд.
   */
  row?: boolean;

  /**
   * Необязательная всплывающая подсказка для отображения с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Определяет, отключена ли группа радио-ввода.
   */
  disabled?: boolean;

  /**
   * Необязательный идентификатор для автоматизированного тестирования.
   */
  testId?: string;

  /**
   * Флаг для отключения отступов.
   */
  withoutMargins?: boolean;

  /**
   * Флаг, указывающий на наличие ошибки.
   */
  error?: boolean;

  /**
   * Вспомогательный текст, отображаемый при ошибке.
   */
  helperText?: string;
}

/**
 * Компонент RadioInput представляет собой настраиваемый радио-ввод с необязательными метками и всплывающими подсказками.
 * Он поддерживает как десктопные, так и мобильные макеты и позволяет настраивать размерование меток.
 *
 * @public
 * @param items - Массив элементов, каждый из которых содержит метку и значение.
 * @param label - Метка для группы радио-ввода.
 * @param labelIcon - Иконка для метки.
 * @param labelPlacement - Размещение метки относительно радиокнопки.
 * @param value - Текущее выбранное значение.
 * @param onChange - Функция обратного вызова, которая вызывается при изменении выбранного значения.
 * @param required - Определяет, является ли группа радио-ввода обязательной.
 * @param row - Определяет, должны ли радиокнопки отображаться в ряд.
 * @param tooltip - Необязательная всплывающая подсказка для отображения с меткой.
 * @param disabled - Определяет, отключена ли группа радио-ввода.
 * @param testId - Необязательный идентификатор для автоматизированного тестирования.
 * @param withoutMargins - Флаг для отключения отступов.
 * @param error - Флаг, указывающий на наличие ошибки.
 * @param helperText - Вспомогательный текст, отображаемый при ошибке.
 * @returns Компонент RadioInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link RadioInputProps}
 */
export const RadioInput = ({
  items,
  label,
  labelIcon,
  labelPlacement = 'end',
  value,
  onChange,
  required = false,
  row = false,
  tooltip = null,
  disabled = false,
  testId,
  withoutMargins = false,
  error,
  helperText,
  ...rest
}: RadioInputProps) => {
  const isDesktop = useIsDesktop();
  const { classes } = useStyles();

  return (
    <FormControl
      className={classNames(
        'aqa_radion_input',
        error && helperText && classes.groupMargin,
      )}
      component="fieldset"
      disabled={disabled}
    >
      {label && (
        <Label
          label={label}
          required={required}
          tooltip={tooltip}
          icon={labelIcon}
        />
      )}

      <RadioGroup
        {...rest}
        data-testid={testId}
        row={isDesktop || row}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        classes={{ root: classes.root }}
        className={classNames(
          'aqa_radioinput_block',
          !error && classes.groupMargin,
          withoutMargins && classes.withoutMargins,
          row && labelPlacement === 'top' && classes.labelTopRow,
          error && classes.error,
        )}
      >
        {items.map((item) => {
          if (item.tooltip && item.disabled) {
            return (
              <Tooltip key={item.label} title={item.tooltip} placement="bottom">
                <Box key={item.value}>
                  <FormControlLabel
                    key={item.value}
                    value={item.value}
                    disabled={item.disabled}
                    classes={{
                      root: classNames(
                        classes.itemRoot,
                        labelPlacement === 'top' && classes.labelTop,
                      ),
                      label: classes.label,
                      disabled: classes.disabled,
                    }}
                    control={
                      <Radio
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        classes={{
                          root: classes.inputRoot,
                          checked: classes.checked,
                        }}
                        icon={
                          <Icon
                            name="radio_off"
                            size="24"
                            color="stroke.primary"
                          />
                        }
                        checkedIcon={<Icon name="radio_on" size="24" />}
                      />
                    }
                    label={
                      <Box
                        mr={labelPlacement === 'start' ? '8' : '0'}
                        ml={labelPlacement === 'end' ? '8' : '0'}
                      >
                        <Typography
                          color="text.light.secondary"
                          variant="body3"
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    }
                    labelPlacement={labelPlacement}
                  />
                </Box>
              </Tooltip>
            );
          } else {
            return (
              <Box key={item.value}>
                <FormControlLabel
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  classes={{
                    root: classNames(
                      classes.itemRoot,
                      labelPlacement === 'top' && classes.labelTop,
                    ),
                    label: classes.label,
                    disabled: classes.disabled,
                  }}
                  control={
                    <Radio
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      classes={{
                        root: classes.inputRoot,
                        checked: classes.checked,
                      }}
                      icon={
                        <Icon
                          name="radio_off"
                          size="24"
                          color="stroke.primary"
                        />
                      }
                      checkedIcon={<Icon name="radio_on" size="24" />}
                    />
                  }
                  label={
                    <Box
                      mr={labelPlacement === 'start' ? '8' : '0'}
                      ml={labelPlacement === 'end' ? '8' : '0'}
                    >
                      <Typography color="text.light.secondary" variant="body3">
                        {item.label}
                      </Typography>
                    </Box>
                  }
                  labelPlacement={labelPlacement}
                />
              </Box>
            );
          }
        })}
      </RadioGroup>

      {error && (
        <Box mt="4">
          <Typography variant="caption" color="accent.text.error">
            {helperText}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};
